import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { PersonRef } from '@apis/reports';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import { useTradeLabel } from '@hooks/useTradeLabel';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatTimeRange } from '@utils/dateUtils';
import classes from './PersonnelRow.module.css';

interface IPersonnelRowProps {
  person: PersonRef;
  /** Stop-level fallback time range. Rendered only when the BFF didn't
   * supply per-tag `workPeriods` (snapshot data missing or degenerate). */
  fallbackTimeText: string;
}

/**
 * Stop-level personnel row. Mirrors Trusstor's `TagOnLocationRow`:
 *   <name>  <entity pill>                          <time range>
 *                                                  <time range>
 *   [clipboard] <tagId>  ·  <profession>
 *
 * A tag with multiple presence intervals in the same stop renders one
 * time line per period (Trusstor's `<div className={classes.tagsTime}>`
 * stacks them vertically). Profession appears inline on the sub-line
 * when present; the entity pill is suppressed for named workers
 * (`entityType === 'none'`).
 *
 * Wrapped in `memo` because personnel lists can have 20+ rows and the
 * containing `StopDetail` re-renders when the activities query settles —
 * the props are primitives/stable refs from TanStack Query's cache, so
 * shallow comparison short-circuits correctly.
 */
function PersonnelRowImpl({ person, fallbackTimeText }: IPersonnelRowProps) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const tradeOf = useTradeLabel();
  const showPill = person.entityType !== 'none';
  const entityLabel = showPill
    ? t(`reports.dailyRoute.entityType.${person.entityType}`)
    : '';
  // Resolve the opaque tradeId to a translated trade name — matches what
  // `SubjectCard` shows in the report header for the same person. Without
  // this we'd render raw "tr-elec" text where the rest of the page reads
  // "Electrical".
  const professionLabel = tradeOf(person.professionTradeId);
  // Memoize the per-period time-range strings — `formatTimeRange` invokes
  // Luxon's tz lookup + format for each period, called per row per render
  // unless cached against the stable `workPeriods` reference.
  const periodTexts = useMemo<string[]>(() => {
    if (person.workPeriods.length > 0) {
      return person.workPeriods.map((p) => formatTimeRange(p.startIso, p.endIso, tz));
    }
    return fallbackTimeText ? [fallbackTimeText] : [];
  }, [person.workPeriods, tz, fallbackTimeText]);
  return (
    <li className={classes.row}>
      <div className={classes.main}>
        <div className={classes.topLine}>
          <span className={classes.name}>{person.name}</span>
          {showPill && <span className={classes.entityPill}>{entityLabel}</span>}
        </div>
        <div className={classes.subLine}>
          <CardIcon name="tag" size={14} />
          <span className={classes.tagId}>{person.tagId}</span>
          {professionLabel && (
            <>
              <span aria-hidden="true" className={classes.divider}>·</span>
              <span className={classes.profession}>{professionLabel}</span>
            </>
          )}
        </div>
      </div>
      {periodTexts.length > 0 && (
        <div className={classes.time}>
          {periodTexts.map((txt, i) => (
            <span key={i} className={classes.timeLine}>
              {txt}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

export const PersonnelRow = memo(PersonnelRowImpl);
